import {
  IReportDetail,
  TimeseriesDataV1,
  TimeseriesDataV2,
} from "@shared/lib/types/reports";
import { formatData } from "@shared/lib/utils/formatData";
import { Card } from "@shared/ui";
import { Flex } from "antd";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  TooltipItem,
} from "chart.js";
import { Instagram, Video, Youtube } from "lucide-react";
import { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  CHART_METRICS,
  ChartMetricKey,
  DEFAULT_CHART_METRIC,
  ENGAGEMENT_METRIC,
} from "./chartMetrics";
import styles from "./ReportAggregatedData.module.scss";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface ReportAggregatedDataProps {
  aggregatedData: IReportDetail["aggregateData"];
  totalMedia: number;
  timeSeriesData: TimeseriesDataV1 | TimeseriesDataV2;
  reportVersion?: "v1" | "v2";
}

function isTimeseriesV2(
  data: TimeseriesDataV1 | TimeseriesDataV2,
  reportVersion?: "v1" | "v2"
): data is TimeseriesDataV2 {
  if (reportVersion === "v2") return true;
  if (reportVersion === "v1") return false;
  const firstEntry = Object.values(data)[0];
  return !!firstEntry && "deltaViews" in firstEntry;
}

function formatDateLabel(dateStr: string) {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;

  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${month} ${day}, ${hours}:${minutes}`;
}

export function ReportAggregatedData({
  aggregatedData,
  totalMedia,
  timeSeriesData,
  reportVersion,
}: ReportAggregatedDataProps) {
  const isV2 = isTimeseriesV2(timeSeriesData, reportVersion);
  const [selectedMetric, setSelectedMetric] =
    useState<ChartMetricKey>(DEFAULT_CHART_METRIC);

  const activeMetric = CHART_METRICS[selectedMetric];

  const { chartData, mediaCounts } = useMemo(() => {
    if (!timeSeriesData || Object.keys(timeSeriesData).length === 0) {
      return {
        chartData: { labels: [] as string[], datasets: [] },
        mediaCounts: [] as number[],
      };
    }

    const sortedEntries = Object.entries(timeSeriesData).sort(
      ([dateA], [dateB]) =>
        new Date(dateA).getTime() - new Date(dateB).getTime()
    );

    const labels = sortedEntries.map(([date]) => formatDateLabel(date));
    const metricConfig = CHART_METRICS[selectedMetric];

    if (isV2) {
      const seriesData = sortedEntries.map(([, data]) =>
        metricConfig.getV2Value(data as TimeseriesDataV2[string])
      );
      const counts = sortedEntries.map(
        ([, data]) => (data as TimeseriesDataV2[string]).noOfMediasWithData || 0
      );

      return {
        chartData: {
          labels,
          datasets: [
            {
              label: metricConfig.chartTitle.v2,
              data: seriesData,
              backgroundColor: metricConfig.color,
              borderRadius: 4,
              borderSkipped: false,
            },
          ],
        },
        mediaCounts: counts,
      };
    }

    const seriesData = sortedEntries.map(([, data]) =>
      metricConfig.getV1Value(data as TimeseriesDataV1[string])
    );

    return {
      chartData: {
        labels,
        datasets: [
          {
            label: metricConfig.chartTitle.v1,
            data: seriesData,
            backgroundColor: metricConfig.color,
            borderRadius: 4,
            borderSkipped: false,
          },
        ],
      },
      mediaCounts: [] as number[],
    };
  }, [timeSeriesData, isV2, selectedMetric]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          right: 16,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "#1a1f36",
          padding: 8,
          titleFont: {
            size: 11,
            weight: "bold" as const,
          },
          bodyFont: {
            size: 11,
            weight: "bold" as const,
          },
          displayColors: false,
          callbacks: {
            label: (context: TooltipItem<"bar">) => {
              const value = context.parsed.y;
              if (value === null) return "";

              const formattedValue = formatData(value);

              if (!isV2) {
                return formattedValue;
              }

              const mediaCount = mediaCounts[context.dataIndex] ?? 0;
              return [
                `${formattedValue} ${selectedMetric}`,
                `${mediaCount} ${mediaCount === 1 ? "media" : "medias"}`,
              ];
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: 10,
            },
            color: "#697386",
            maxRotation: 0,
            minRotation: 0,
          },
        },
        y: {
          position: "left" as const,
          beginAtZero: true,
          grid: {
            display: true,
            color: "#f1f5f9",
          },
          ticks: {
            font: {
              size: 10,
            },
            color: "#697386",
            callback: (value: number | string) => {
              if (typeof value === "number") {
                return formatData(value);
              }
              return value;
            },
          },
        },
      },
    }),
    [isV2, mediaCounts]
  );

  const totalPlatforms =
    aggregatedData?.platforms.reduce((acc, curr) => acc + curr.count, 0) || 1;

  const othersPlatformCount =
    totalPlatforms -
    (aggregatedData?.platforms.find((p) => p.platform === "instagram")?.count ||
      0) -
    (aggregatedData?.platforms.find((p) => p.platform === "youtube")?.count ||
      0);

  const platformData = [
    {
      name: "Instagram",
      percentage: Math.round(
        ((aggregatedData?.platforms.find((p) => p.platform === "instagram")
          ?.count || 0) /
          totalPlatforms) *
          100
      ),
      color: "#ec4899",
      icon: Instagram,
    },
    {
      name: "YouTube",
      percentage: Math.round(
        ((aggregatedData?.platforms.find((p) => p.platform === "youtube")
          ?.count || 0) /
          totalPlatforms) *
          100
      ),
      color: "#dc2626",
      icon: Youtube,
    },
    ...(othersPlatformCount > 0
      ? [
          {
            name: "Others",
            percentage: Math.round(
              (othersPlatformCount / totalPlatforms) * 100
            ),
            color: "#000000",
            icon: Video,
          },
        ]
      : []),
  ];

  const metrics = [
    {
      ...CHART_METRICS.views,
      value: formatData(aggregatedData.social.totalViews, "a"),
      subtext: `From ${totalMedia} `,
      clickable: true as const,
    },
    {
      ...CHART_METRICS.likes,
      value: formatData(aggregatedData.social.totalLikes, "a"),
      clickable: true as const,
    },
    {
      ...CHART_METRICS.comments,
      value: formatData(aggregatedData.social.totalComments, "a"),
      clickable: true as const,
    },
    {
      ...CHART_METRICS.shares,
      value: formatData(aggregatedData.social.totalShares, "a"),
      clickable: true as const,
    },
    {
      ...ENGAGEMENT_METRIC,
      value: `${formatData(aggregatedData.social.engRate, "0.0a")}%`,
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.metricsGrid}>
        {metrics.map((metric) => {
          const IconComponent = metric.icon;
          const isSelected = metric.clickable && metric.key === selectedMetric;
          const cardClassName = [
            styles.metricCard,
            metric.clickable ? styles.metricCardClickable : "",
            isSelected ? styles.metricCardSelected : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <Card
              key={metric.key}
              className={cardClassName}
              styles={{ body: { padding: 0 } }}
              onClick={
                metric.clickable
                  ? () => setSelectedMetric(metric.key)
                  : undefined
              }
            >
              <Flex
                vertical
                justify="space-between"
                className={styles.metricContent}
              >
                <div>
                  <Flex
                    justify="space-between"
                    align="flex-start"
                    className={styles.metricHeader}
                  >
                    <div
                      className={styles.metricIcon}
                      style={{ backgroundColor: metric.bg }}
                    >
                      <IconComponent
                        size={16}
                        style={{ color: metric.color }}
                      />
                    </div>
                  </Flex>
                  <div className={styles.metricInfo}>
                    <p className={styles.metricLabel}>{metric.label}</p>
                    <h3 className={styles.metricValue}>{metric.value}</h3>
                    {"subtext" in metric && metric.subtext && (
                      <p className={styles.metricSubtext}>{metric.subtext}</p>
                    )}
                  </div>
                </div>
              </Flex>
            </Card>
          );
        })}
      </div>

      <div className={styles.chartsRow}>
        <Card className={styles.viewsChartCard}>
          <Flex
            justify="space-between"
            align="center"
            className={styles.chartHeader}
          >
            <h3 className={styles.chartTitle}>
              {isV2 ? activeMetric.chartTitle.v2 : activeMetric.chartTitle.v1}
            </h3>
          </Flex>
          <div className={styles.chartLayout}>
            {chartData.labels.length > 0 ? (
              <div className={styles.chartWrapper}>
                <div
                  className={styles.chartContainer}
                  style={{
                    width: `${Math.max(600, chartData.labels.length * 50)}px`,
                    height: "240px",
                  }}
                >
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
            ) : (
              <div className={styles.emptyChart}>No data available</div>
            )}
          </div>
        </Card>

        <Card className={styles.platformCard}>
          <h3 className={styles.chartTitle}>Platform Split</h3>
          <Flex vertical gap={24} className={styles.platformContent}>
            {platformData.map((plat) => {
              const PlatformIcon = plat.icon;
              return (
                <div key={plat.name}>
                  <Flex
                    justify="space-between"
                    align="center"
                    className={styles.platformHeader}
                  >
                    <Flex gap={8} align="center">
                      <PlatformIcon size={16} className={styles.platformIcon} />
                      <span className={styles.platformName}>{plat.name}</span>
                    </Flex>
                    <span className={styles.platformPercentage}>
                      {plat.percentage}%
                    </span>
                  </Flex>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{
                        width: `${plat.percentage}%`,
                        backgroundColor: plat.color,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </Flex>
        </Card>
      </div>
    </div>
  );
}
